import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

const STAGE_META = [
  { id: 1, label: "Welcome" },
  { id: 2, label: "Preview" },
  { id: 3, label: "Pricing" },
  { id: 4, label: "Marketplace" },
  { id: 5, label: "Subscribe" },
];

const SHORT_VIEWPORT_HEIGHT = 590;
const LAPTOP_BREAKPOINT = 1024;

function getCompactHeight() {
  if (typeof window === "undefined") return false;
  return window.innerHeight <= SHORT_VIEWPORT_HEIGHT;
}

function getIsLaptop() {
  if (typeof window === "undefined") return true;
  return window.innerWidth >= LAPTOP_BREAKPOINT;
}

export default function ConciergeStageShell({
  stage,
  setStage,
  canAdvance = true,
  onFinish,
  children,
}) {
  const [compactHeight, setCompactHeight] = useState(getCompactHeight);
  const [isLaptop, setIsLaptop] = useState(getIsLaptop);

  useEffect(() => {
    // Only lock the viewport on laptop+. On smaller screens we want natural
    // page scrolling so users can reach every stage's CTA (including the
    // Subscribe button on Stage 5).
    if (!isLaptop) return undefined;
    const main = document.querySelector("main");
    const html = document.documentElement;
    const body = document.body;
    const saved = {
      html: html.style.overflow,
      body: body.style.overflow,
      main: main?.style.overflow,
      mainHeight: main?.style.height,
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (main) {
      main.style.overflow = "hidden";
      main.style.height = "100dvh";
    }
    return () => {
      html.style.overflow = saved.html;
      body.style.overflow = saved.body;
      if (main) {
        main.style.overflow = saved.main || "";
        main.style.height = saved.mainHeight || "";
      }
    };
  }, [isLaptop]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => {
      setCompactHeight(getCompactHeight());
      setIsLaptop(getIsLaptop());
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const next = () => {
    if (!canAdvance) return;
    if (stage >= 5) {
      onFinish?.();
      return;
    }
    setStage(stage + 1);
  };
  const prev = () => {
    if (stage <= 1) return;
    setStage(stage - 1);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /input|textarea|select/i.test(e.target.tagName)) return;
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, canAdvance]);

  const currentLabel = useMemo(
    () => STAGE_META.find((s) => s.id === stage)?.label || "",
    [stage]
  );

  const renderedChildren = useMemo(() => {
    if (!React.isValidElement(children)) return children;
    return React.cloneElement(children, { compactHeight });
  }, [children, compactHeight]);

  return (
    <div
      className="relative w-full min-h-[100dvh] laptop:h-[100dvh] laptop:overflow-hidden bg-[#FAFAFA]"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Ambient tint layer */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 600px at 85% 0%, rgba(239,73,73,0.06), transparent 60%), radial-gradient(900px 500px at 0% 100%, rgba(17,17,17,0.04), transparent 60%)",
        }}
      />

      <div className="relative laptop:h-full grid grid-rows-[auto_auto_auto] laptop:grid-rows-[auto_minmax(0,1fr)_auto]">
        {/* Top bar */}
        <div
          className={`flex items-center justify-between ${
            compactHeight
              ? "px-4 laptop:px-8 pt-3 laptop:pt-4"
              : "px-5 laptop:px-10 pt-5 laptop:pt-6"
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#888888]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
              Bellhopt Concierge
            </span>
            <span className="hidden laptop:inline text-[11px] text-[#888888]">/</span>
            <span className="hidden laptop:inline text-[11px] font-semibold text-[#5F6368]">
              {currentLabel}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              disabled={stage <= 1}
              aria-label="Previous stage"
              className={`rounded-full bg-white border border-[#EAEAEA] text-[#111111] hover:bg-[#F7F7F8] disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center ${
                compactHeight ? "w-9 h-9" : "w-10 h-10"
              }`}
            >
              <ArrowLeft size={15} strokeWidth={2} />
            </button>
            <button
              onClick={next}
              disabled={!canAdvance}
              aria-label="Next stage"
              className={`inline-flex items-center gap-1.5 rounded-full bg-[#111111] text-white font-bold hover:bg-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors ${
                compactHeight ? "h-9 px-3.5 text-[11px]" : "h-10 px-4 text-[12px]"
              }`}
            >
              {stage >= 5 ? "Subscribe" : "Continue"}
              <ArrowRight size={13} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Stage content */}
        <div className="relative laptop:min-h-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={stage}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
              className="relative w-full laptop:absolute laptop:inset-0"
            >
              {renderedChildren}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer — dots */}
        <div
          className={`flex items-center justify-center gap-1.5 ${
            compactHeight ? "pb-3 laptop:pb-4" : "pb-5 laptop:pb-6"
          }`}
        >
          {STAGE_META.map((s) => {
            const active = s.id === stage;
            const done = s.id < stage;
            return (
              <button
                key={s.id}
                onClick={() => setStage(s.id)}
                aria-label={`Go to stage ${s.id} — ${s.label}`}
                className="group relative h-2 rounded-full transition-all"
                style={{
                  width: active ? 28 : 8,
                  backgroundColor: active ? "#111111" : done ? "#888888" : "#EAEAEA",
                }}
              >
                <span className="sr-only">{s.label}</span>
                <span className="opacity-0 group-hover:opacity-100 absolute -top-7 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-[0.22em] text-[#888888] whitespace-nowrap transition-opacity hidden laptop:inline">
                  {s.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

ConciergeStageShell.STAGE_META = STAGE_META;
